'use client'

import { imageQuality, urlForImage } from '@repo/sanity/image'
import { ProductMedia } from '@repo/sanity/shorthand'
import { cn } from '@repo/ui/lib/utils'
import { Image } from 'next-sanity/image'
import { FunctionComponent, startTransition, useEffect, useMemo } from 'react'
import { useProduct, useUpdateURL } from '../../../providers/ProductProvider'
import { PageDto } from '../../../types/page.types'
import { ProductDto } from '../../../types/product.types'
import Video from '../../media/Video'
import ProductMediaLightbox from './ProductMediaLightbox'
import ProductThumbnails from './ProductThumbnails'

interface Props {
  pageDto: PageDto
  productDto: ProductDto
}

const ProductMediaGallery: FunctionComponent<Props> = ({ pageDto, productDto }) => {
  const updateURL = useUpdateURL()
  const { state, galleryRef, updateLightboxMedia, updateCarouselMedia } = useProduct()
  const mediaIndex = state.media ? parseInt(state.media) : undefined

  const { media, gridPlacement } = useMemo(() => {
    const media: ProductMedia[] = []
    const gridPlacement: boolean[] = []
    const images = (productDto?.sanityProduct?.images || []).flatMap((image) => (image?.asset?.url ? image : []))
    const videos = (productDto?.sanityProduct?.videos || []).flatMap((video) => ((video as any)?.[0]?.videoUrl || (video as any)?.[0]?.videoFile?.asset?.url ? video : []))
    const layout = productDto?.sanityProduct?.mediaLayout || 'mosaic'

    const safePush = (item: ProductMedia | undefined, large: boolean) => {
      if (item) {
        media.push(item)
        gridPlacement.push(large)
      }
    }

    // Insert videos every 4th slot (after every 3 images).
    const combinedMedia: ProductMedia[] = []
    const videoQueue = [...videos]

    images.forEach((img, index) => {
      combinedMedia.push(img)

      const position = index + 1
      const shouldInsertVideo = position % 3 === 0 && videoQueue.length > 0

      if (shouldInsertVideo) {
        combinedMedia.push(videoQueue.shift()!)
      }
    })

    // Add any remaining videos.
    combinedMedia.push(...videoQueue)
    const count = combinedMedia.length

    if (layout === 'stack') {
      combinedMedia.forEach((item) => safePush(item, true))

      return { media, gridPlacement }
    }

    if (count === 0) {
      return { media, gridPlacement }
    }

    // First item must always be an image.
    const firstImage = images[0]
    safePush(firstImage, true)

    let i = 1
    let itemCountSinceLastLarge = 1

    while (i < count) {
      const remaining = count - i

      if (remaining === 1) {
        safePush(combinedMedia[i], true)
        break
      }

      if (remaining === 2) {
        safePush(combinedMedia[i], false)
        safePush(combinedMedia[i + 1], false)
        break
      }

      if (itemCountSinceLastLarge >= 4) {
        safePush(combinedMedia[i], true)
        itemCountSinceLastLarge = 1
        i++
        continue
      }

      safePush(combinedMedia[i], false)
      safePush(combinedMedia[i + 1], false)
      i += 2
      itemCountSinceLastLarge += 2
    }

    return { media, gridPlacement }
  }, [productDto?.sanityProduct?.images, productDto?.sanityProduct?.videos, productDto?.sanityProduct?.mediaLayout])

  // Update state 'media' URL param upon carousel scroll.
  useEffect(() => {
    const handleScroll = () => {
      if (!galleryRef.current) {
        return
      }

      const scrollLeft = galleryRef.current.scrollLeft
      const containerWidth = galleryRef.current.offsetWidth
      const newIndex = Math.round(scrollLeft / containerWidth)

      if (newIndex !== mediaIndex) {
        startTransition(() => {
          const newState = updateCarouselMedia(newIndex.toString())
          updateURL(newState)
        })
      }
    }

    const scrollContainer = galleryRef.current

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [galleryRef, mediaIndex, updateCarouselMedia, updateURL])

  return (
    <form className='relative'>
      <ul
        ref={galleryRef}
        className='bg-card max-lg:scrollbar-hide flex aspect-[2/3] w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden scroll-smooth lg:grid lg:aspect-auto lg:size-full lg:grid-cols-2 lg:bg-transparent'
      >
        {media?.map((item, i) => {
          const isLarge = gridPlacement[i]
          const isVideo = 'videos' in item
          const videoMobile: any = isVideo && 'videos' in item ? item?.videos?.[0] : undefined
          const videoDesktop: any = isVideo && 'videos' in item ? item?.videos?.[1] : undefined
          const videoUrlMobile = videoMobile?.videoUrl || videoMobile?.videoFile?.asset?.url
          const videoUrlDesktop = videoDesktop?.videoUrl || videoDesktop?.videoFile?.asset?.url

          return (
            <li key={i} className={cn('bg-card flex aspect-[2/3] w-full shrink-0 snap-start', isLarge && 'lg:col-span-2')} data-type={isVideo ? 'video' : 'image'}>
              <button
                className='focus-visible:outline-hidden relative size-full cursor-zoom-in'
                aria-label={pageDto.dictionary.lightbox?.view || 'View'}
                formAction={() => {
                  const newState = updateLightboxMedia(i.toString())

                  updateURL(newState)
                }}
              >
                {isVideo && videoUrlMobile && (
                  <Video
                    src={videoUrlMobile}
                    className={cn('absolute inset-0', videoUrlDesktop && 'lg:hidden')}
                    poster={urlForImage(videoMobile?.videoPoster)?.width(600)?.url()}
                    aria-label={videoMobile?.videoPoster?.alt}
                  />
                )}
                {isVideo && videoUrlDesktop && (
                  <Video
                    src={videoUrlDesktop}
                    className={cn('absolute inset-0 hidden lg:block')}
                    poster={urlForImage(videoDesktop?.videoPoster)?.width(600)?.url()}
                    aria-label={videoDesktop?.videoPoster?.alt}
                  />
                )}
                {!isVideo && '_type' in item && (
                  <Image
                    fill={true}
                    priority={i === 0}
                    quality={imageQuality}
                    className='object-cover'
                    alt={item?.alt || 'Miista'}
                    src={item.asset?.url || ''}
                    sizes={`(min-width: 1024px) ${isLarge ? '50vw' : '25vw'}, 100vw`}
                    blurDataURL={item.asset?.metadata?.lqip ?? undefined}
                    placeholder={item.asset?.metadata?.lqip ? 'blur' : undefined}
                  />
                )}
              </button>
            </li>
          )
        })}
      </ul>
      <ProductThumbnails pageDto={pageDto} media={media} />
      <ProductMediaLightbox pageDto={pageDto} media={media} />
    </form>
  )
}

export default ProductMediaGallery
